import React from "react"
import { Link } from "gatsby"
import Box from "@mui/material/Box"
import Container from "@mui/material/Container"
import Typography from "@mui/material/Typography"
import MuiLink from "@mui/material/Link"
import Divider from "@mui/material/Divider"
import { FaWhatsapp } from "react-icons/fa"
import { MdPhone, MdEmail } from "react-icons/md"

import { useT } from "i18n/translations"
import { alpha, palette } from "theme/colors"
import { anchorId, isAnchor, isTopAnchor, scrollToSection, scrollToTop } from "utils/navLinks"

const HEADER_OFFSET = 72

/**
 * Footer for the one-page site.
 *
 * Nav links mirror the header: in-page anchors for every section, real routes
 * only for the legal pages. Anchors must render as <a href="#…"> rather than a
 * Gatsby <Link>, which would treat "#solucoes" as a route.
 */
const Footer = ({ lang, navItems = [], siteSettings = {}, allLanguages = [] }) => {
  const tr = useT(lang)
  const year = new Date().getFullYear()

  const address = siteSettings.address || ""
  const phone = siteSettings.phone || process.env.GATSBY_PHONE || ""
  const email = siteSettings.email || process.env.GATSBY_EMAIL || ""
  const whatsappNumber = (
    siteSettings.whatsapp ||
    process.env.GATSBY_WHATSAPP ||
    ""
  ).replace(/[^\d]/g, "")
  const complaintsUrl =
    siteSettings.complaintsUrl || "https://www.livroreclamacoes.pt/inicio"

  const footerLinks = navItems.filter(
    (i) => i.type === "normal" || i.type === "soon" || !i.type,
  )

  const linkSx = {
    color: alpha.textMuted,
    textDecoration: "none",
    fontSize: "15px",
    fontWeight: 400,
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    "&:hover": { color: palette.onDark },
    transition: "color 0.15s",
  }

  const legalSx = {
    color: alpha.textFaint,
    textDecoration: "none",
    fontSize: "13px",
    "&:hover": { color: palette.onDark },
    transition: "color 0.15s",
  }

  const columnTitleSx = {
    fontSize: "11px",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: palette.onDark,
    mb: 3,
  }

  const onAnchor = (url) => (event) => {
    if (!isAnchor(url)) return
    event.preventDefault()
    if (isTopAnchor(url)) return scrollToTop()
    scrollToSection(anchorId(url), HEADER_OFFSET)
  }

  /** Renders a WP menu item as an anchor or a route, per its URL. */
  const navLink = (item, i) => {
    const url = item?.link?.url || ""
    const label = item?.link?.title || ""

    if (item.type === "soon") {
      return (
        <Box
          key={i}
          aria-disabled="true"
          sx={{ display: "flex", alignItems: "center", gap: 1, opacity: 0.45 }}
        >
          <Typography
            component="span"
            sx={{ ...linkSx, cursor: "default", pointerEvents: "none" }}
          >
            {label}
          </Typography>
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              px: 1,
              py: 0.25,
              borderRadius: "100px",
              bgcolor: alpha.accentSoft,
              color: palette.accent,
              flexShrink: 0,
            }}
          >
            {tr.nav.soon}
          </Box>
        </Box>
      )
    }

    if (isAnchor(url)) {
      return (
        <MuiLink key={i} href={url} onClick={onAnchor(url)} sx={linkSx} underline="none">
          {label}
        </MuiLink>
      )
    }

    return (
      <MuiLink key={i} component={Link} to={url} sx={linkSx} underline="none">
        {label}
      </MuiLink>
    )
  }

  return (
    <Box component="footer" sx={{ bgcolor: palette.primary, color: palette.onDark }}>
      <Container
        maxWidth="xl"
        sx={{ px: { xs: 4, md: 8 }, pt: { xs: 6, md: 10 }, pb: { xs: 4, md: 6 } }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "2fr 1fr 1fr" },
            gap: { xs: 6, lg: 8 },
            mb: 6,
          }}
        >
          {/* Brand */}
          <Box>
            <Box sx={{ mb: 3 }}>
              <img
                src="/images/logo.png"
                alt={siteSettings.siteTitle || "AC Solar"}
                style={{ height: 36, filter: "brightness(0) invert(1)" }}
              />
            </Box>
            <Typography
              sx={{
                color: alpha.textSecondary,
                fontSize: "16px",
                lineHeight: 1.6,
                maxWidth: "34ch",
                mb: address ? 2 : 0,
              }}
            >
              {tr.footer.tagline}
            </Typography>
            {address && (
              <Typography
                sx={{
                  color: alpha.textMuted,
                  fontSize: "14px",
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                {address}
              </Typography>
            )}
          </Box>

          {/* Section index — mirrors the header nav */}
          <Box>
            <Typography sx={columnTitleSx}>{tr.footer.navigation}</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start" }}>
              {footerLinks.map(navLink)}
            </Box>
          </Box>

          {/* Contact */}
          <Box>
            <Typography sx={columnTitleSx}>{tr.footer.contact}</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start" }}>
              {phone && (
                <MuiLink href={`tel:${phone.replace(/\s/g, "")}`} sx={linkSx} underline="none">
                  <MdPhone aria-hidden="true" />
                  {phone}
                </MuiLink>
              )}
              {whatsappNumber && (
                <MuiLink
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={linkSx}
                  underline="none"
                >
                  <FaWhatsapp aria-hidden="true" />
                  {tr.footer.whatsapp}
                </MuiLink>
              )}
              {email && (
                <MuiLink href={`mailto:${email}`} sx={linkSx} underline="none">
                  <MdEmail aria-hidden="true" />
                  {email}
                </MuiLink>
              )}
              {!phone && !email && !whatsappNumber && (
                <Typography sx={{ ...linkSx, opacity: 0.6 }}>
                  {/* Set GATSBY_PHONE / GATSBY_EMAIL / GATSBY_WHATSAPP in .env */}
                  —
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: alpha.hairline }} />

        {/* Bottom bar — copyright + the only real routes on the site */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
            pt: 3,
          }}
        >
          <Typography sx={{ fontSize: "13px", color: alpha.textFaint }}>
            © {year} {tr.footer.copyright}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 1.5, sm: 3 },
            }}
          >
            {/* The only real routes on the site. Both are WP pages on the
                Default template, built by src/templates/general/Page.js.
                Do not add a link here without a page behind it. */}
            <MuiLink component={Link} to="/politica-de-privacidade/" sx={legalSx} underline="none">
              {tr.footer.privacy}
            </MuiLink>
            <MuiLink component={Link} to="/termos-e-condicoes/" sx={legalSx} underline="none">
              {tr.footer.terms}
            </MuiLink>
            <MuiLink
              href={complaintsUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={legalSx}
              underline="none"
            >
              {tr.footer.complaints}
            </MuiLink>

            {allLanguages.length > 1 &&
              allLanguages.map((l) => (
                <MuiLink
                  key={l.code}
                  component={Link}
                  to={l.urlPrefix || "/"}
                  underline="none"
                  sx={{
                    ...legalSx,
                    color:
                      l.code === lang?.toUpperCase()
                        ? palette.white
                        : alpha.textFaint,
                    fontWeight: l.code === lang?.toUpperCase() ? 600 : 400,
                  }}
                >
                  {l.code}
                </MuiLink>
              ))}
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default Footer
